import { useCallback, useEffect, useReducer, useRef } from 'react';

import { buildVisitorWebchatIdentity } from 'modules/webchat/domain/helpers';
import { useWebchat } from 'modules/webchat/presentation/contexts/WebchatContext';
import {
  PRE_CHAT_INITIAL_STATE,
  transitionPreChat,
} from 'modules/webchat/presentation/helpers';
import getWebchatTracking from 'modules/webchat/presentation/helpers/getWebchatTracking';
import { IPreChatFormValues } from 'modules/webchat/presentation/validations';
import { COUNTRY_CODES } from 'shared/presentation/constants';
import { useLocale } from 'shared/presentation/contexts';

type TParams = {
  /** Chamado quando o pré-chat termina, com o chat aberto. */
  onFinish(): void;
};

/**
 * O pré-chat só coleta nome, celular e aceite e abre o chat com eles. A
 * identificação da pessoa (cliente, cadastrado ou novo) e a criação do lead
 * ficam com o bot da Callbell, que busca o cadastro pelo celular no Vectis.
 * No clique, o pré-chat captura de onde a pessoa veio (UTMs, ids de clique,
 * referrer, IP e página), e o bot grava isso no lead.
 */
export default function usePreChat({ onFinish }: TParams) {
  const { language } = useLocale('webchat');
  const { status, mountWebchat, openWebchat } = useWebchat();

  const [state, dispatch] = useReducer(
    transitionPreChat,
    PRE_CHAT_INITIAL_STATE,
  );

  const isActive = useRef(true);
  const openRequested = useRef(false);
  const onFinishRef = useRef(onFinish);

  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  useEffect(
    () => () => {
      isActive.current = false;
    },
    [],
  );

  const submit = useCallback(
    async ({ name, phone }: IPreChatFormValues) => {
      dispatch({ type: 'SUBMITTED' });

      const tracking = await getWebchatTracking();

      if (!isActive.current) return;

      mountWebchat(
        buildVisitorWebchatIdentity({
          name,
          phone,
          countryCode: COUNTRY_CODES[language],
          tracking,
        }),
      );
    },
    [language, mountWebchat],
  );

  useEffect(() => {
    if (
      state.step !== 'openingChat' ||
      status === 'idle' ||
      openRequested.current
    ) {
      return;
    }

    openRequested.current = true;

    // O openWebchat espera o widget ficar pronto e avisa a pessoa se ele falhar.
    openWebchat().finally(() => {
      if (isActive.current) onFinishRef.current();
    });
  }, [openWebchat, state.step, status]);

  return {
    step: state.step,
    isFormVisible: state.step === 'form',
    submit,
  };
}
